import { useCallback, useEffect, useState } from "react";
import { theme, card, btnPrimary, inputStyle, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate } from "../lib/format";
import { StatusTag, type StatusTone } from "./Drawing";
import { PortalLink } from "./PortalLink";
import { ModalHeader, ModalOverlay, secondaryBtn } from "../pages/resilience/shared";

// Where a data subject's data probably sits (lib/dsrDataLocation.ts on the server): a checklist
// built by rule from the record of processing, its systems, processors and data discovery
// locations, for the kind of person the handler picks. The server never sees or returns who the
// person is, and neither does anything here: the processor letter leaves a placeholder for the
// handler to fill in in their own mail client.

interface Owner { userId: string | null; name: string | null }
interface ChecklistItem {
  id: string; itemKey: string; kind: string; refId: string; label: string; reasons: string[]; need: string; needText: string;
  keepReason: string | null; standing: string | null; status: string; note: string | null; closedAt: string | null;
  closedBy: string | null; owner: Owner; href: string | null;
}
interface PreviewItem { itemKey: string; kind: string; label: string; reasons: string[]; need: string }
export interface ChecklistReadiness { total: number; closed: number; open: number; built: boolean; ready: boolean }
interface ChecklistResponse {
  subjectCategory: string | null;
  builtAt: string | null;
  options: Array<{ id: string; label: string; group: boolean }>;
  readiness: ChecklistReadiness;
  items: ChecklistItem[];
  preview: PreviewItem[] | null;
}

const NEED_LABELS: Record<string, string> = {
  copy: "Copy", delete: "Delete", keep: "Restrict and keep", correct: "Correct", export: "Export",
  not_portable: "Portability does not apply", restrict: "Restrict", stop: "Stop processing", review: "Review the decision", search: "Search",
};
const KIND_LABELS: Record<string, string> = {
  system: "System", data_set: "Personal data set", location: "Found by data discovery", processor: "Processor", activity: "Processing activity",
};
const STATUS: Record<string, { tone: StatusTone; label: string }> = {
  open: { tone: "none", label: "To do" },
  done: { tone: "met", label: "Done" },
  not_needed: { tone: "partial", label: "Not needed" },
};

const smallBtn: React.CSSProperties = { ...secondaryBtn, padding: "5px 12px", fontSize: 12 };
const linkBtn: React.CSSProperties = { background: "none", border: "none", padding: 0, font: "inherit", color: "var(--c-accentText)", cursor: "pointer" };

/** "3 of 5 places dealt with", for the request list. */
export function checklistSummary(r: ChecklistReadiness | undefined): string | null {
  if (!r?.built) return null;
  return r.total === 0 ? "No places found" : `${r.closed} of ${r.total} place${r.total === 1 ? "" : "s"} dealt with`;
}

export function DsrDataChecklist({ requestId, isOpen, canEdit, onChanged }: {
  requestId: string;
  /** A closed request keeps its checklist as it was. */
  isOpen: boolean;
  canEdit: boolean;
  onChanged?: () => void;
}) {
  const [data, setData] = useState<ChecklistResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [category, setCategory] = useState("");
  const [ticking, setTicking] = useState<{ id: string; status: string; note: string } | null>(null);
  const [letter, setLetter] = useState<{ label: string; text: string } | null>(null);

  const load = useCallback((preview?: string) => {
    api.get<ChecklistResponse>(`/gdpr/dsr/${requestId}/checklist${preview ? `?preview=${encodeURIComponent(preview)}` : ""}`)
      .then((d) => { setData(d); setCategory((c) => c || d.subjectCategory || ""); })
      .catch((e) => setError(apiErrorMessage(e, "Could not load the checklist.")));
  }, [requestId]);
  useEffect(() => { load(); }, [load]);

  async function build() {
    if (!category) return;
    setBusy("build"); setError(null);
    try {
      const d = await api.post<ChecklistResponse>(`/gdpr/dsr/${requestId}/checklist`, { subjectCategory: category });
      setData({ ...d, preview: null });
      onChanged?.();
    } catch (e) { setError(apiErrorMessage(e, "Could not build the checklist.")); }
    finally { setBusy(null); }
  }

  async function saveTick(e: React.FormEvent) {
    e.preventDefault();
    if (!ticking) return;
    setBusy(ticking.id); setError(null);
    try {
      const d = await api.patch<ChecklistResponse>(`/gdpr/dsr/${requestId}/checklist/${ticking.id}`, { status: ticking.status, note: ticking.note || null });
      setData({ ...d, preview: null });
      setTicking(null);
      onChanged?.();
    } catch (err) { setError(apiErrorMessage(err, "Could not save the item.")); }
    finally { setBusy(null); }
  }

  async function reopen(item: ChecklistItem) {
    setBusy(item.id); setError(null);
    try {
      const d = await api.patch<ChecklistResponse>(`/gdpr/dsr/${requestId}/checklist/${item.id}`, { status: "open" });
      setData({ ...d, preview: null });
      onChanged?.();
    } catch (err) { setError(apiErrorMessage(err, "Could not reopen the item.")); }
    finally { setBusy(null); }
  }

  async function openLetter(item: ChecklistItem) {
    setError(null);
    try {
      const d = await api.get<{ text: string }>(`/gdpr/dsr/${requestId}/checklist/${item.id}/letter`);
      setLetter({ label: item.label, text: d.text });
    } catch (err) { setError(apiErrorMessage(err, "Could not write the letter.")); }
  }

  if (!data) return error ? <div role="alert" style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div> : null;
  const built = !!data.builtAt;
  const r = data.readiness;
  const editable = canEdit && isOpen;

  return (
    <section style={{ ...card, padding: 0, minWidth: 0 }} aria-label="Where the person's data is">
      <header style={{ padding: "8px 14px", borderBottom: "1px solid var(--c-line)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <h3 style={{ ...text.sectionTitle, margin: 0 }}>Where the person's data is</h3>
        {built && (
          <StatusTag tone={r.ready ? "met" : r.closed > 0 ? "partial" : "none"}>
            {r.total === 0 ? "No places found" : r.ready ? "Every place dealt with" : `${r.closed} of ${r.total} dealt with`}
          </StatusTag>
        )}
      </header>
      <div style={{ padding: "10px 14px", display: "grid", gap: 10 }}>
        <div style={{ ...text.hint, maxWidth: 760 }}>
          The places your records say this kind of person's data is held: the systems and personal data sets of each processing activity that covers them,
          what data discovery found there, and the processors to ask. Worked out from the registers alone; who the person is never leaves this request.
        </div>
        {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}

        {editable && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ ...text.hint, display: "grid", gap: 4 }}>Kind of person
              <select value={category} onChange={(e) => { setCategory(e.target.value); if (!built && e.target.value) load(e.target.value); }} style={{ ...inputStyle, width: 240 }}>
                <option value="">Choose…</option>
                {data.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </label>
            <button type="button" disabled={!category || busy !== null} onClick={build}
              style={built ? smallBtn : { ...btnPrimary, padding: "6px 14px", fontSize: 12 }}>
              {busy === "build" ? "Working…" : built ? "Rebuild the checklist" : "Build the checklist"}
            </button>
            {built && <span style={{ ...text.meta }}>A rebuild keeps every place already ticked off.</span>}
          </div>
        )}

        {!built && data.preview && (
          <div style={{ ...text.hint }}>
            {data.preview.length === 0
              ? "No processing activity in the record covers this kind of person. Add the activity to the record of processing, or choose another kind."
              : `This would list ${data.preview.length} place${data.preview.length === 1 ? "" : "s"}: ${data.preview.map((p) => p.label).join(", ")}.`}
          </div>
        )}
        {!built && !editable && <div style={{ ...text.hint }}>No checklist was built for this request.</div>}

        {built && data.items.length === 0 && (
          <div style={{ ...text.hint }}>Nothing in the record of processing covers this kind of person. Search wherever else they might appear, and add what you find to the record.</div>
        )}
        {data.items.map((i) => {
          const s = STATUS[i.status] ?? STATUS.open;
          const isTicking = ticking?.id === i.id;
          return (
            <div key={i.id} style={{ border: `1px solid ${i.need === "keep" ? "var(--c-warning)" : "var(--c-border)"}`, borderRadius: 3, padding: "10px 12px" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <StatusTag tone={s.tone}>{s.label}</StatusTag>
                {i.href ? <PortalLink to={i.href} style={{ ...text.body, fontWeight: 600, color: "var(--c-accentText)" }}>{i.label}</PortalLink>
                  : <span style={{ ...text.body, fontWeight: 600 }}>{i.label}</span>}
                <span style={{ ...text.meta }}>{KIND_LABELS[i.kind] ?? i.kind}</span>
                <span style={{ ...text.hint, fontWeight: 600, color: theme.color.text }}>{NEED_LABELS[i.need] ?? i.need}</span>
              </div>
              <div style={{ ...text.hint, color: theme.color.text, marginTop: 4 }}>{i.needText}</div>
              {i.keepReason && <div style={{ ...text.hint, marginTop: 4 }}>Kept because {i.keepReason}.</div>}
              <ul style={{ margin: "4px 0 0", paddingLeft: 18, ...text.hint }}>
                {i.reasons.map((why) => <li key={why}>{why}</li>)}
              </ul>
              <div style={{ ...text.meta, marginTop: 4 }}>
                Owner: {i.owner.name ?? "not recorded"}
                {i.status !== "open" && i.closedAt ? ` · ${STATUS[i.status]?.label.toLowerCase()} ${fmtDate(i.closedAt)}${i.closedBy ? ` by ${i.closedBy}` : ""}` : ""}
              </div>
              {i.note && <div style={{ ...text.hint, color: theme.color.text, marginTop: 4 }}>Note: {i.note}</div>}

              {editable && !isTicking && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  {i.status === "open" ? (
                    <>
                      <button type="button" style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12 }} disabled={busy !== null}
                        onClick={() => setTicking({ id: i.id, status: "done", note: i.note ?? "" })}>Mark done</button>
                      <button type="button" style={smallBtn} disabled={busy !== null}
                        onClick={() => setTicking({ id: i.id, status: "not_needed", note: i.note ?? "" })}>Not needed</button>
                    </>
                  ) : (
                    <button type="button" style={smallBtn} disabled={busy !== null} onClick={() => reopen(i)}>Reopen</button>
                  )}
                  {i.kind === "processor" && <button type="button" style={linkBtn} onClick={() => openLetter(i)}>Letter to the processor</button>}
                </div>
              )}
              {!editable && i.kind === "processor" && canEdit && (
                <button type="button" style={{ ...linkBtn, marginTop: 6 }} onClick={() => openLetter(i)}>Letter to the processor</button>
              )}
              {isTicking && ticking && (
                <form onSubmit={saveTick} style={{ display: "grid", gap: 8, marginTop: 10 }}>
                  <label style={{ ...text.hint, display: "grid", gap: 4 }}>
                    {ticking.status === "not_needed" ? "Why this place needs no action (required)" : "What was done (optional)"}
                    <textarea rows={2} required={ticking.status === "not_needed"} maxLength={2000} value={ticking.note}
                      onChange={(e) => setTicking({ ...ticking, note: e.target.value })} style={{ ...inputStyle, resize: "vertical" }} />
                  </label>
                  <div style={{ ...text.meta }}>Keep the person's name and contact details out of the note: say what was done, not who it was for.</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="submit" style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12 }} disabled={busy !== null}>{busy === i.id ? "Saving…" : "Save"}</button>
                    <button type="button" style={smallBtn} onClick={() => setTicking(null)}>Cancel</button>
                  </div>
                </form>
              )}
            </div>
          );
        })}
      </div>

      {letter && (
        <ModalOverlay onClose={() => setLetter(null)}>
          <ModalHeader title={`Letter to ${letter.label}`} onClose={() => setLetter(null)} />
          <div style={{ padding: 16, display: "grid", gap: 10 }}>
            <div style={{ ...text.hint }}>
              Copy this into your own email. Replace the bracketed line with only what the processor needs to find the person; the platform never adds it and sends nothing.
            </div>
            <textarea readOnly rows={16} aria-label="Letter text" value={letter.text} style={{ ...inputStyle, ...text.mono, resize: "vertical" }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" style={smallBtn} onClick={() => { void navigator.clipboard?.writeText(letter.text); }}>Copy</button>
              <button type="button" style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12 }} onClick={() => setLetter(null)}>Close</button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </section>
  );
}
